import { useEffect, useMemo, useState } from 'react';

import { CallSplit } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { CampaignChannels, Send } from 'domains/campaigns/types';
import FormLayout from 'domains/core/components/FormLayout';
import Select, { getOptionValues, Value } from 'domains/core/components/Select';
import ActivityFilter from 'domains/segments/pages/SegmentCreatePage/Filter/ActivityFilter';
import DemographicFilter from 'domains/segments/pages/SegmentCreatePage/Filter/DemographicFilter';
import EventFilter from 'domains/segments/pages/SegmentCreatePage/Filter/EventFilter';
import { isValidEventsFilter } from 'domains/segments/pages/SegmentCreatePage/utils';
import {
    SegmentFilterCategorySpecificField,
    SegmentFilterEngagementFields,
    SegmentFilterEventFields,
    SegmentFilterState,
} from 'domains/segments/types';
import { getDemographicFilters, getEngagementNameToValuesRef } from 'domains/segments/utils';
import useSegmentFilters from 'hooks/queries/useSegmentFilters';
import useSendById from 'hooks/queries/useSendById';
import useSends from 'hooks/queries/useSends';
import useQueryParams from 'hooks/useQueryParams';
import { CampaignStatus, FilterCategory, RecordType } from 'models/enums';
import { getFriendlyName } from 'utils';

import DisabledTooltipContent, { DisabledTooltip } from '../../../components/DisabledTooltipContent';
import { CustomCampaignSteps } from '../../../CustomCampaign';
import { setSegment } from '../../../customCampaignActions';
import { CampaignStateTypes } from '../../../customCampaignReducer';

const allContactsRecordType = {
    label: RecordType.ALL_CONTACTS,
    value: RecordType.ALL_CONTACTS,
};

const initialFilterState: SegmentFilterState<SegmentFilterCategorySpecificField> = {
    id: 0,
    category: FilterCategory.CONTACT_ACTIVITY,
    name: '',
    operator: null,
    categorySpecificFields: {
        value: null,
    },
    autocompleteValue: null,
    autocompleteInputValue: '',
};

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setNextStep: (step: CustomCampaignSteps) => void;
};

const CustomCampaignSplitCriteriaStep = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setIsContinueEnabled,
    setNextStep,
}: Props) => {
    const activeSend = campaignState.sends[activeSendIndex];
    const isActiveSendEmail = activeSend?.channel === CampaignChannels.EMAIL;

    const initialCategory =
        isActiveSendEmail && (activeSend?.segments?.[0]?.category || FilterCategory.CONTACT_ACTIVITY);
    const [category, setCategory] = useState(initialCategory);
    const [filter, setFilter] = useState(initialFilterState);
    const [datetimeErrors, setDatetimeErrors] = useState({});

    // When a sentinel value is selected (unsaved same-curation send), resolve its templateId so
    // ValueAutocomplete can extract clickable links from the template HTML without a DB record.
    const selectedSendValue = isActiveSendEmail && (filter as any)?.categorySpecificFields?.value;
    const sentinelTemplateId = useMemo(() => {
        if (typeof selectedSendValue === 'number' && selectedSendValue < 0) {
            const idx = -selectedSendValue - 1;
            return (campaignState.sends[idx] as any)?.templateId ?? undefined;
        }
        return undefined;
    }, [selectedSendValue, campaignState.sends]);

    const activeSendValueUsed = isActiveSendEmail && (activeSend as any)?.segments?.[0]?.categorySpecificFields?.value;
    // Negative values are sentinels for unsaved campaign sends — only fetch by ID for real (positive) send IDs
    const realSendId = typeof activeSendValueUsed === 'number' && activeSendValueUsed > 0 ? activeSendValueUsed : null;
    const { data: sendByIdData } = useSendById(realSendId as number, {
        enabled: Boolean(realSendId),
    });
    const { contactObject: hasContactObjectFlag } = useFlags();

    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;
    const disableEditing = status === CampaignStatus.PAUSED;

    // Update filter autocomplete values when send data is available
    useEffect(() => {
        if (realSendId && sendByIdData?.name) {
            filter.autocompleteInputValue = sendByIdData.name;
            filter.autocompleteValue = { label: sendByIdData.name, value: realSendId };
            setFilter(filter);
        } else if (typeof activeSendValueUsed === 'number' && activeSendValueUsed < 0) {
            // Sentinel value: resolve display name from campaign state using the encoded parent index
            const parentIdx = -activeSendValueUsed - 1;
            const parentSend = campaignState.sends[parentIdx];
            if (parentSend) {
                const name = (parentSend as any).name || '';
                filter.autocompleteInputValue = name;
                filter.autocompleteValue = { label: name, value: activeSendValueUsed };
                setFilter(filter);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [realSendId, sendByIdData, activeSendValueUsed]);

    const filterCategories: Value[] = Object.values(FilterCategory)
        .filter((category) => hasContactObjectFlag || category !== FilterCategory.CONTACT_OBJECTS)
        .map((category) => ({ label: category, value: category }));

    const { data: segmentFilters } = useSegmentFilters({
        version: 2,
    });

    const engagementFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        // RecordTypes[0] represents engagement filters because there's a single item and its recordType value is "" from the API
        return segmentFilters.engagement.recordTypes[0].filters;
    }, [segmentFilters]);

    const engagementNameToValuesRef = useMemo(() => getEngagementNameToValuesRef(engagementFilters), [
        engagementFilters,
    ]);

    // Derived directly from filter state so it's always reactive — no ref needed.
    const sendsPath = useMemo(() => (filter?.name && engagementNameToValuesRef[filter.name]) || '', [
        filter?.name,
        engagementNameToValuesRef,
    ]);

    const { data: sends } = useSends({
        path: sendsPath,
        reactQueryOptions: { enabled: Boolean(sendsPath) },
    });

    // Parent email sends from this curation (before the current split send).
    // Sends without a DB id (brand-new unsaved campaign) get a negative sentinel -(i+1) so they
    // still appear in the dropdown; the sentinel is emitted as null at submit time and the
    // backend resolves it to the real parentSend.id inside the create-campaign transaction.
    const parentEmailSendsFromState = useMemo(
        (): Send[] =>
            campaignState.sends
                .slice(0, activeSendIndex)
                .filter((send) => send.channel === CampaignChannels.EMAIL)
                .map((send, i) => {
                    const existingId = (send as any).id;
                    return ({
                        id: existingId ?? -(i + 1),
                        name: (send as any).name || '',
                        status: (send as any).status || 'DRAFT',
                    } as unknown) as Send;
                }),
        [campaignState.sends, activeSendIndex]
    );

    // Merge API sends with parent sends from campaign state, deduplicating by id.
    // This ensures the current curation's sends appear in the dropdown regardless of their
    // DB status (DRAFT sends are excluded by the API query's status filter).
    const allSendsForDropdown = useMemo((): Send[] => {
        const apiSends = sends || [];
        const apiSendIds = new Set(apiSends.map((s) => s.id));
        const uniqueParentSends = parentEmailSendsFromState.filter((s) => !apiSendIds.has(s.id));
        return [...uniqueParentSends, ...apiSends];
    }, [parentEmailSendsFromState, sends]);

    useEffect(() => {
        // Find EMAIL send with segments (may be different from activeSendIndex)
        let segmentsToUse: any[] | undefined;

        if (isActiveSendEmail && (activeSend as any)?.segments?.length) {
            segmentsToUse = (activeSend as any).segments;
        } else {
            // Search all sends for EMAIL send with segments
            const sendWithSegments = campaignState.sends.find(
                (send) =>
                    send.channel === CampaignChannels.EMAIL && 'segments' in send && (send as any).segments?.length
            ) as any;
            segmentsToUse = sendWithSegments?.segments;
        }

        // @ts-ignore
        if (segmentsToUse?.length) {
            const firstSegment = segmentsToUse[0];
            setFilter(firstSegment);
            if (firstSegment?.category) {
                setCategory(firstSegment.category);
            }
        }

        setNextStep(CustomCampaignSteps.SELECT_PATH_ACTION);
        // The dependency array only contains `activeSendIndex` because we only want it to run
        // if the component rerenders when we're switching from one split to another in the nav.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSendIndex]);

    useEffect(() => {
        // TODO: Replace with existing filter validation "isFilterValid" from SegmentCreatePage/utils.ts
        const isValidActivitySegment = filter?.category === FilterCategory.CONTACT_ACTIVITY && !!filter?.operator;
        const isValidEventSegment =
            (filter?.category === FilterCategory.CONTACT_EVENTS ||
                filter?.category === FilterCategory.CONTACT_OBJECTS) &&
            isValidEventsFilter(filter);

        const hasDatetimeError = Object.values(datetimeErrors).includes(true);
        const isValidDemographicSegment =
            filter?.category === FilterCategory.CONTACT_DETAILS &&
            !!filter?.operator &&
            (!!filter?.autocompleteInputValue?.length || !!filter?.autocompleteValue) &&
            !hasDatetimeError;

        setIsContinueEnabled(isValidActivitySegment || isValidDemographicSegment || isValidEventSegment);
    }, [filter, setIsContinueEnabled, datetimeErrors]);

    const eventFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        const titleCasedRecordTypeFilters = segmentFilters.contactEvent.recordTypes?.map(({ recordType, filters }) => ({
            filters,
            recordType: getFriendlyName(recordType),
        }));
        return titleCasedRecordTypeFilters;
    }, [segmentFilters]);

    const objectFilters = useMemo(() => {
        if (!segmentFilters || !hasContactObjectFlag) {
            return [];
        }
        const titleCasedRecordTypeFilters = segmentFilters.contactObject.recordTypes?.map(
            ({ recordType, filters }) => ({
                filters,
                recordType: getFriendlyName(recordType),
            })
        );
        return titleCasedRecordTypeFilters;
    }, [segmentFilters, hasContactObjectFlag]);

    const recordTypes = getOptionValues([allContactsRecordType]);
    const segmentRecordTypes = segmentFilters?.demographic?.recordTypes;
    const demographicFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        const result = getDemographicFilters({ hasAllContacts: true, recordTypes, segmentRecordTypes });

        return result;
    }, [segmentFilters, recordTypes, segmentRecordTypes]);

    const handleCategorySelection = (e: any) => {
        setCategory(e.target.value);
        setFilter({
            ...initialFilterState,
            category: e.target.value,
        });
    };

    const handleDatetimeError = (filterId: string, error: boolean) =>
        setDatetimeErrors({ ...datetimeErrors, [filterId]: error });

    const handleUpdateSegmentFilter = (
        _index: number,
        filter: SegmentFilterState<SegmentFilterCategorySpecificField>
    ) => {
        setFilter(filter);
        dispatchCampaignAction(setSegment(filter, activeSendIndex));
    };

    return (
        <FormLayout
            header="select split criteria"
            stepName="conditional split"
            icon={<CallSplit />}
            guidingText="Create a split in your Curation based on audience behavior."
        >
            <Stack alignItems="flex-start" columnGap={2} direction="row" flexWrap="wrap" rowGap={2}>
                <DisabledTooltipContent disabled={disableEditing}>
                    <span>
                        <Select
                            label="Filter type"
                            disabled={disableEditing}
                            sx={{ textTransform: 'capitalize', width: '12rem' }}
                            value={category}
                            values={filterCategories}
                            onChange={handleCategorySelection}
                        />
                    </span>
                </DisabledTooltipContent>
                {category === FilterCategory.CONTACT_ACTIVITY && engagementFilters && (
                    <ActivityFilter
                        filter={filter as SegmentFilterState<SegmentFilterEngagementFields>}
                        index={0}
                        engagementFilters={engagementFilters}
                        sends={allSendsForDropdown}
                        sx={{ autocomplete: { width: '300px' } }}
                        templateId={sentinelTemplateId}
                        updateSegmentFilter={handleUpdateSegmentFilter}
                        disabled={disableEditing}
                        disabledTooltipContent={<DisabledTooltip />}
                    />
                )}
                {category === FilterCategory.CONTACT_DETAILS && demographicFilters && (
                    <DisabledTooltipContent disabled={disableEditing}>
                        <Stack direction="row" sx={disableEditing ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
                            <DemographicFilter
                                filter={filter}
                                index={0}
                                filterOptions={demographicFilters}
                                handleDatetimeError={handleDatetimeError}
                                updateSegmentFilter={handleUpdateSegmentFilter}
                            />
                        </Stack>
                    </DisabledTooltipContent>
                )}
                {category === FilterCategory.CONTACT_EVENTS && eventFilters && (
                    <DisabledTooltipContent disabled={disableEditing}>
                        <Stack direction="row" sx={disableEditing ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
                            <EventFilter
                                eventRecordTypeFilters={eventFilters}
                                filter={filter as SegmentFilterState<SegmentFilterEventFields>}
                                index={0}
                                handleDatetimeError={handleDatetimeError}
                                isResponsive
                                updateSegmentFilter={handleUpdateSegmentFilter}
                            />
                        </Stack>
                    </DisabledTooltipContent>
                )}
                {hasContactObjectFlag && category === FilterCategory.CONTACT_OBJECTS && objectFilters && (
                    <DisabledTooltipContent disabled={disableEditing}>
                        <Stack direction="row" sx={disableEditing ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
                            <EventFilter
                                eventRecordTypeFilters={objectFilters}
                                filter={filter as SegmentFilterState<SegmentFilterEventFields>}
                                index={0}
                                handleDatetimeError={handleDatetimeError}
                                isResponsive
                                updateSegmentFilter={handleUpdateSegmentFilter}
                            />
                        </Stack>
                    </DisabledTooltipContent>
                )}
            </Stack>
        </FormLayout>
    );
};

export default CustomCampaignSplitCriteriaStep;
